import {
  type ComponentType,
  memo,
  useCallback,
  useMemo,
  useState
} from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import TestAutoplayingDarkVideo from '../../../static/videos/autoplay-test-dark.mp4';
import { PlayIcon } from '../../icons/Icons';
import {
  DEFAULT_OPACITY,
  DEFAULT_TRANSITION,
  HEX_DEMO_AUTOPLAY,
  HEX_DEMO_VIDEO,
  TRACKING_LABELS
} from '../../utils';
import { Button } from '../design-system';
import { FloatyDarkButton } from '../design-system/FloatyDarkButton';
import { FullScreenVideo } from '../global/FullScreenVideo';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Product = styled.div<{ $isAnimating: boolean }>`
  display: flex;
  border-radius: 3px;
  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  background: ${({ theme }) => theme.backgroundColor};
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.6);
  width: 100%;
  overflow: hidden;
  position: relative;
  transition:
    box-shadow 0.2s ${({ theme }) => theme.animation.EASING},
    border-color 0.2s ${({ theme }) => theme.animation.EASING};

  ${({ $isAnimating, theme }) =>
    $isAnimating &&
    css`
      border: 1px solid ${({ theme }) => rgba(theme.borderColor.LOUD, 0.5)};
      box-shadow:
        0 4px 24px 0 rgba(0, 0, 0, 0.6),
        0 2px 15px 0 ${rgba(theme.highlightColor, 0.35)};
    `}
`;

const ButtonWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 4;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
`;

const ButtonInner = styled.div`
  @property --x {
    syntax: '<number>';
    initial-value: 0;
    inherits: false;
  }
  @property --rotation {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }

  --radius: 62px;
  --rotation: 180deg;
  --x: 1;
  --length: 0;
  --duration: 3.6s;

  display: flex;
  position: relative;
  border-radius: var(--radius);
  padding: 1px;

  // Perfect Shadow twitter thread:
  // https://x.com/jh3yy/status/1796164809953513560
  box-shadow: var(--perfect-shadow);
  --base: hsl(240 17% 9% / 15%);
  --shade: hsl(from var(--base) calc(h + 20) 25 calc(l - 5));
  --perfect-shadow: 0 0 0 1px var(--base), 0 1px 1px -0.5px var(--shade),
    0 3px 3px -1.5px var(--shade), 0 6px 6px -3px var(--shade),
    0 12px 12px -6px var(--base), 0 24px 24px -12px var(--base);

  @keyframes bgRotate {
    0% {
      --x: 0;
    }
    33.333% {
      --x: 1;
      --rotation: 0deg;
    }
    50% {
      --x: 1;
      --rotation: 180deg;
    }
    83.3333% {
      --x: 0;
      --rotation: 180deg;
    }
    100% {
      --rotation: 360deg;
    }
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 9999px;
    animation: bgRotate var(--duration) linear infinite;
    background-image: conic-gradient(
      from var(--rotation) at
        calc(var(--radius) * 0.5 + (var(--x) * (100% - var(--radius)))) 50%,
      ${({ theme }) => theme.borderColor.MUTED} 0.8turn,
      ${({ theme }) => theme.highlightColor} 1turn
    );
    z-index: 2;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform-origin: center;
    border-radius: 9999px;
    animation: bgRotate var(--duration) linear infinite;
    filter: blur(5px);
    background-image: conic-gradient(
      from var(--rotation) at
        calc(var(--radius) * 0.5 + (var(--x) * (100% - var(--radius)))) 50%,
      rgba(0, 0, 0, 0) 0.65turn,
      ${({ theme }) => theme.colors.PURPLE} 1turn
    );
  }
`;

const DemoTime = styled.span`
  opacity: 0.5;
`;

const VideoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 16 / 9;
  position: relative;

  img {
    position: absolute;
    inset: 0;
    object-fit: cover;
    width: 100%;
    display: block;
  }
`;

const Video = styled.video<{ $visible: boolean }>`
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity
    ${({ theme }) => `${theme.animation.DURATION} ${theme.animation.EASING}`};

  ${({ $visible }) =>
    $visible &&
    css`
      opacity: 1;
      pointer-events: all;
    `}
`;

const Synopsis = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  z-index: 2;
  gap: 150px;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 150px 20px 20px 30px;
  background-image: linear-gradient(
    180deg,
    ${({ theme }) => rgba(theme.backgroundColor, 0)} 0%,
    ${({ theme }) => theme.backgroundColor} 100%
  );
`;

interface WorkflowType {
  id: number;
  isOverview?: boolean;
  synopsis?: string;
  title: string;
  video: string;
}

const ANIMATIONS = {
  VideoButton: {
    initial: { opacity: DEFAULT_OPACITY },
    transition: {
      ...DEFAULT_TRANSITION,
      delay: 0.2
    },
    viewport: { once: true },
    whileInView: { opacity: 1 }
  },
  FullScreenVideo: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { ...DEFAULT_TRANSITION }
  }
};

const WORKFLOWS: WorkflowType[] = [
  {
    id: 0,
    title: 'Overview',
    video: HEX_DEMO_AUTOPLAY,
    isOverview: true
  },
  {
    id: 1,
    title: 'Exploratory analysis',
    video: TestAutoplayingDarkVideo,
    synopsis:
      'Bacon ipsum dolor amet shoulder pastrami pancetta kielbasa andouille, shank short ribs sausage tenderloin meatloaf cupim strip steak chislic drumstick salami.'
  },
  {
    id: 2,
    title: 'Interactive data apps',
    video: TestAutoplayingDarkVideo,
    synopsis:
      'Pastrami tongue bacon meatloaf fatback corned beef kielbasa pork loin capicola swine salami t-bone shankle ribeye.'
  },
  {
    id: 3,
    title: 'Data science',
    video: TestAutoplayingDarkVideo,
    synopsis:
      'Flank salami prosciutto corned beef boudin ball tip venison landjaeger beef andouille doner drumstick kevin meatball pastrami.'
  },
  {
    id: 4,
    title: 'No-code analysis',
    video: TestAutoplayingDarkVideo,
    synopsis:
      'Chislic swine pork loin tri-tip beef ribs pancetta fatback, meatloaf shankle pig burgdoggen tongue drumstick capicola doner.'
  },
  {
    id: 5,
    title: 'Reporting',
    video: TestAutoplayingDarkVideo,
    synopsis:
      'Pork pork chop bresaola short ribs tongue kevin, sirloin ham hock ball tip pork loin.'
  }
];

export const WorkflowsTout: ComponentType = memo(() => {
  const [isAnimating] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [currentWorkflow] = useState<number>(0);

  const thisWorkflow = useMemo(
    () => WORKFLOWS[currentWorkflow],
    [currentWorkflow]
  );

  const _handlePlayVideo = useCallback(() => {
    setIsVideoOpen(true);
  }, []);

  const _handleCloseVideo = useCallback(() => {
    setIsVideoOpen(false);
  }, []);

  // const heroPlaceholder = (
  //   <img
  //     alt="Hex hero placeholder"
  //     src="/images/home-poster.png"
  //     style={{ width: '100%', height: '100%' }}
  //   />
  // );

  return (
    <Wrapper>
      {/* {!shouldCollapse && (
        <Nav>
          {WORKFLOWS.map((item, index) => (
            <NavItem
              key={index}
              $active={currentWorkflow === item.id}
              onClick={() => onChangeWorkflow(item)}>
              {item.title}
            </NavItem>
          ))}
        </Nav>
      )} */}
      <Product $isAnimating={isAnimating}>
        {thisWorkflow.isOverview && (
          <ButtonWrapper {...ANIMATIONS.VideoButton} onClick={_handlePlayVideo}>
            <ButtonInner>
              <FloatyDarkButton
                detail={<DemoTime>5 min</DemoTime>}
                icon={<PlayIcon />}
                text="Watch a full demo"
              />
            </ButtonInner>
          </ButtonWrapper>
        )}
        <VideoWrapper>
          <Helmet>
            <link
              as="image"
              fetchPriority="high"
              href="/images/home/home-poster/1050.webp"
              // @ts-expect-error this is a valid link attr
              imagesizes="(min-width: 1240px) 1160px,
                (min-width: 601px) calc(100vw - 160px),
                calc(100vw - 80px)"
              imagesrcset="/images/home/home-poster/1920.webp 1920w, /images/home/home-poster/1050.webp 1050w, /images/home/home-poster/780.webp 780w, /images/home/home-poster/400.webp 400w"
              rel="preload"
            />
          </Helmet>
          <img
            alt="Hex Demo"
            sizes="(min-width: 1240px) 1160px,
                (min-width: 601px) calc(100vw - 160px),
                calc(100vw - 80px)"
            src="/images/home/home-poster/1050.webp"
            srcSet="/images/home/home-poster/1920.webp 1920w, /images/home/home-poster/1050.webp 1050w, /images/home/home-poster/780.webp 780w, /images/home/home-poster/400.webp 400w"
          />
          <Video
            $visible={true}
            autoPlay={true}
            controls={false}
            loop={true}
            muted={true}
            playsInline={true}
            poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7">
            <source src={WORKFLOWS[currentWorkflow].video} type="video/mp4" />
          </Video>
          {/* <LazyLoad
            height="100%"
            offset={50}
            once={true}
            placeholder={heroPlaceholder}>
            {WORKFLOWS.map((item, index) => (
              <Video
                key={`${index}-video`}
                $visible={currentWorkflow === item.id}
                autoPlay={true}
                controls={false}
                loop={true}
                muted={true}
                playsInline={true}>
                <source src={item.video} type="video/mp4" />
              </Video>
            ))}
          </LazyLoad> */}
          {!thisWorkflow.isOverview && thisWorkflow.synopsis && (
            <Synopsis>
              <div>{thisWorkflow.synopsis}</div>
              <Button
                icon={<PlayIcon />}
                text="Watch a demo"
                onClick={_handlePlayVideo}
              />
            </Synopsis>
          )}
        </VideoWrapper>
      </Product>
      <FullScreenVideo
        isOpen={isVideoOpen}
        path={HEX_DEMO_VIDEO}
        trackingLabel={TRACKING_LABELS.VideoPlayed}
        onClose={_handleCloseVideo}
      />
    </Wrapper>
  );
});
